import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import wash from 'storybookConfig/mixins/wash';
import Logo from 'storybookConfig/stories/molecules/Logo';
import ShaderBackground, {
  type ShaderPalette,
} from 'storybookConfig/stories/molecules/ShaderBackground';
import { ThemeBreakpoints, ThemeMotion, type ThemeColor } from 'storybookConfig/stories/theme';

/**
 * The frame for the sign-in flow: the wordmark and the page in a white panel, the website's
 * plasma field beside it. Below `md` it is the panel alone.
 *
 * Pages that share an `AuthStage` share one panel and one field. When the next page puts the
 * panel on the other side, the panel flows across like liquid (its edges bow with its speed and
 * wobble as it settles), the old page fades out on it, the new one fades in, and the field blends
 * into the new page's palette. An `AuthLayout` outside a stage brings its own.
 */

/** How long the panel takes to cross; the page fades are timed against it. */
export const AUTH_TRANSITION_MS = 770;

/* Text comes in once the panel has landed, one `AuthReveal` step after another. */
const REVEAL_STEP_MS = 125;
const REVEAL_MS = 350;
const REVEAL_STEPS = 6;
/* Cards take longer than text, so they settle slowly. */
const CARD_MS = 1000;
const SETTLE_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)'; // ease-out cubic: no jump at the start

/** How long a page stays in its `enter` (and the old one in its `leave`) phase. */
export const AUTH_SETTLE_MS = AUTH_TRANSITION_MS + REVEAL_STEPS * REVEAL_STEP_MS + CARD_MS;

const PANEL_WIDTH = 520;
const GAP = 12; // the white margin around the field
const RADIUS = 22; // theme.radius['2xl']
/* The edges' bow chases the panel's speed on a spring: `pull` px of bow per px/ms of speed. */
const SPRING = { stiffness: 0.0012, damping: 0.03, pull: 5.4 };

export type AuthSide = 'left' | 'right';
export type AuthPagePhase = 'idle' | 'enter' | 'leave';

interface Look {
  side: AuthSide;
  palette: ShaderPalette;
}

const StageContext = createContext<((look: Look) => void) | null>(null);
const PhaseContext = createContext<AuthPagePhase>('idle');

/*
 * The white panel as a path in page pixels: a column from `x` to `x + width`, with concave
 * fillets where it meets the field so the field keeps its rounded corners, and side edges that
 * bow by `bow` px (positive bows right).
 */
const panelPath = (x: number, width: number, height: number, bow: number) => {
  const top = GAP + RADIUS;
  const bottom = height - GAP - RADIUS;
  const third = (bottom - top) / 3;
  const edge = (at: number, from: number, to: number) => {
    const reach = at + bow * 1.33;
    const dir = Math.sign(to - from);
    return `C${reach} ${from + dir * third} ${reach} ${to - dir * third} ${at} ${to}`;
  };
  const left = x;
  const right = x + width;
  return [
    `M${left - RADIUS} 0 H${right + RADIUS} V${GAP}`,
    `A${RADIUS} ${RADIUS} 0 0 0 ${right} ${top}`,
    edge(right, top, bottom),
    `A${RADIUS} ${RADIUS} 0 0 0 ${right + RADIUS} ${height - GAP}`,
    `V${height} H${left - RADIUS} V${height - GAP}`,
    `A${RADIUS} ${RADIUS} 0 0 0 ${left} ${bottom}`,
    edge(left, bottom, top),
    `A${RADIUS} ${RADIUS} 0 0 0 ${left - RADIUS} ${GAP} Z`,
  ].join(' ');
};

const StageRoot = styled.div`
  position: relative;
  min-height: 100vh;
  background: ${({ theme }) => theme.color.surfaceCanvas};

  @media screen and (${ThemeBreakpoints.md}) {
    height: 100vh;
    overflow: hidden;
  }
`;

/* The still wash under the canvas is what shows before the first frame and without WebGL. */
const Art = styled.div`
  display: none;

  @media screen and (${ThemeBreakpoints.md}) {
    display: block;
    position: absolute;
    inset: ${GAP}px;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.radius['2xl']};
    ${wash('inkwell')};
  }
`;

const Sheet = styled.svg`
  display: none;

  @media screen and (${ThemeBreakpoints.md}) {
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    fill: ${({ theme }) => theme.color.surfaceCanvas};
    pointer-events: none;
  }
`;

interface AuthStageProps {
  children: ReactNode;
  /** Draw the plasma field. Off, the still wash under it shows instead and no WebGL is used. */
  shader?: boolean;
}

/** Holds the panel and the field across the pages inside it, so they can move between them. */
export const AuthStage = ({ children, shader = true }: AuthStageProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [look, setLook] = useState<Look | null>(null);
  const motion = useRef({ x: 0, bow: 0, bowVelocity: 0, frame: 0, placed: false });

  const draw = () => {
    const root = rootRef.current;
    if (!root) return;
    const { x, bow } = motion.current;
    root.style.setProperty('--auth-panel-x', `${x}px`);
    pathRef.current?.setAttribute('d', panelPath(x, PANEL_WIDTH, root.clientHeight, bow));
  };

  const restingX = (side: AuthSide) =>
    side === 'left' ? 0 : (rootRef.current?.clientWidth ?? PANEL_WIDTH) - PANEL_WIDTH;

  // The first page places the panel; a later page on the other side sends it across.
  useLayoutEffect(() => {
    if (!look) return undefined;
    const m = motion.current;
    const from = m.x;
    const to = restingX(look.side);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!m.placed || reduced || from === to) {
      m.placed = true;
      m.x = to;
      draw();
      return undefined;
    }

    let start = 0;
    let last = 0;
    const step = (now: number) => {
      start ||= now;
      const dt = Math.min(32, now - (last || now - 16));
      last = now;
      const t = Math.min(1, (now - start) / AUTH_TRANSITION_MS);
      const eased = t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2;
      const x = from + (to - from) * eased;
      const speed = (x - m.x) / dt;
      m.x = x;
      m.bowVelocity +=
        (SPRING.stiffness * (speed * SPRING.pull - m.bow) - SPRING.damping * m.bowVelocity) * dt;
      m.bow += m.bowVelocity * dt;
      if (t === 1 && Math.abs(m.bow) < 0.2 && Math.abs(m.bowVelocity) < 0.01) {
        m.bow = 0;
        m.bowVelocity = 0;
        m.frame = 0;
      } else {
        m.frame = requestAnimationFrame(step);
      }
      draw();
    };
    m.frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(m.frame);
      m.frame = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look?.side]);

  // The stage is the viewport, so a window resize is the only resize: keep a resting panel
  // against its edge and the sheet the height of the page.
  useEffect(() => {
    if (!look) return undefined;
    const onResize = () => {
      if (!motion.current.frame) motion.current.x = restingX(look.side);
      draw();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look?.side]);

  const show = useCallback(
    (next: Look) =>
      setLook((current) =>
        current?.side === next.side && current.palette === next.palette ? current : next
      ),
    []
  );

  return (
    <StageRoot ref={rootRef}>
      <Art>{look && shader && <ShaderBackground palette={look.palette} />}</Art>
      <Sheet aria-hidden="true">
        <path ref={pathRef} />
      </Sheet>
      <StageContext.Provider value={show}>{children}</StageContext.Provider>
    </StageRoot>
  );
};

interface AuthStagePageProps {
  children: ReactNode;
  phase: AuthPagePhase;
}

/** One page on a stage: `enter` while it comes in, `leave` while the next one does. */
const AuthStagePage = ({ children, phase }: AuthStagePageProps) => (
  <PhaseContext.Provider value={phase}>{children}</PhaseContext.Provider>
);

AuthStage.Page = AuthStagePage;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const fadeOut = keyframes`
  to { opacity: 0; }
`;

/*
 * A fade over part of the crossing, as fractions of it. The old page fades out in the first
 * third; the new one fades in once the panel has landed. The aside sits under the sheet and
 * fades out before the panel covers it; its text comes in through `AuthReveal`.
 */
const fade = (phase: AuthPagePhase, enter: [number, number], leave: [number, number]) => {
  if (phase === 'idle') return undefined;
  const [delay, length] = phase === 'enter' ? enter : leave;
  return css`
    animation: ${phase === 'enter' ? fadeIn : fadeOut} ${AUTH_TRANSITION_MS * length}ms
      ${ThemeMotion.easeOut} ${AUTH_TRANSITION_MS * delay}ms both;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
};

const Foreground = styled.div<{ phase: AuthPagePhase }>`
  display: ${({ phase }) => (phase === 'leave' ? 'none' : 'block')};

  @media screen and (${ThemeBreakpoints.md}) {
    display: block;
    position: absolute;
    inset: 0;
    pointer-events: none;
  }

  /* With motion reduced nothing fades, so the page being left would sit under the new one. */
  @media (prefers-reduced-motion: reduce) {
    ${({ phase }) => phase === 'leave' && 'display: none;'}
  }
`;

/* The page's part of the panel; it rides `--auth-panel-x`, which the stage moves. */
const Panel = styled.main<{ phase: AuthPagePhase }>`
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding: 32px 24px;

  @media screen and (${ThemeBreakpoints.md}) {
    position: absolute;
    inset: 0 auto 0 0;
    width: ${PANEL_WIDTH}px;
    padding: 48px 56px;
    overflow-y: auto;
    z-index: 2;
    transform: translateX(var(--auth-panel-x, 0));
    pointer-events: ${({ phase }) => (phase === 'leave' ? 'none' : 'auto')};
  }
`;

const Brand = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.color.bodyTextPrimary};
`;

const Content = styled.div<{ phase: AuthPagePhase }>`
  width: 100%;
  max-width: 400px;
  margin-block: auto;
  text-wrap: pretty;
  ${({ phase }) => fade(phase, [1, REVEAL_MS / AUTH_TRANSITION_MS], [0, 0.35])}
`;

/* What the page sets over the field, on the side the panel is not. From `lg`: between `md` and `lg`
   the field beside the 520px panel is too narrow for the 60px statement, so it shows bare. */
const Aside = styled.aside<{ phase: AuthPagePhase; side: AuthSide }>`
  display: none;

  @media screen and (${ThemeBreakpoints.lg}) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    inset: ${GAP}px;
    ${({ side }) => (side === 'left' ? 'left' : 'right')}: ${PANEL_WIDTH}px;
    padding: 64px clamp(32px, 6vw, 96px);
    overflow-y: auto;
    color: ${({ theme }) => theme.color.bodyTextOnDark};
    pointer-events: ${({ phase }) => (phase === 'leave' ? 'none' : 'auto')};

    & > * {
      max-width: 880px; // two balanced lines of the 60px statement
    }
    ${({ phase }) => phase === 'leave' && fade(phase, [0, 0], [0, 0.4])}
  }
`;

/* Text drifts up out of a soft blur. */
const settleIn = keyframes`
  from { opacity: 0; transform: translateY(12px); filter: blur(4px); }
  to { opacity: 1; transform: none; filter: none; }
`;

/* A card drifts down into place: no blur or scale, just a slow, soft settle. */
const dropIn = keyframes`
  from { opacity: 0; transform: translateY(-24px); }
  to { opacity: 1; transform: none; }
`;

export type AuthRevealMotion = 'settle' | 'drop';

const MOTIONS = {
  settle: { name: settleIn, ms: REVEAL_MS + 150 },
  drop: { name: dropIn, ms: CARD_MS },
};

const Reveal = styled.div<{ phase: AuthPagePhase; step: number; motion: AuthRevealMotion }>`
  ${({ phase, step, motion }) =>
    phase === 'enter' &&
    css`
      animation: ${MOTIONS[motion].name} ${MOTIONS[motion].ms}ms ${SETTLE_EASE}
        ${AUTH_TRANSITION_MS + step * REVEAL_STEP_MS}ms both;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

interface AuthRevealProps {
  children: ReactNode;
  /** Its place in the sequence, from 0; fractions are fine. */
  step: number;
  /** `settle` for text, `drop` for a card. */
  motion?: AuthRevealMotion;
}

/** A piece of aside content that comes in after the panel lands, in `step` order. */
export const AuthReveal = ({ children, step, motion = 'settle' }: AuthRevealProps) => (
  <Reveal phase={useContext(PhaseContext)} step={step} motion={motion}>
    {children}
  </Reveal>
);

/*
 * The auth pages' type, from the website's form pages (carro-website `FormSplitPage`) with the
 * two display steps swapped for the narrower panel: the statement over the field at the
 * website's title step (60px), the panel's heading at its large heading step (48px), and a lede
 * in Inter.
 */

/** The panel's heading: "Sign in to your Carro account". */
export const AuthTitle = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.text.display.size};
  line-height: ${({ theme }) => theme.text.display.leading};
  letter-spacing: ${({ theme }) => theme.tracking.text};
  text-wrap: balance;
  color: ${({ theme }) => theme.color.bodyTextPrimary};

  @media screen and (${ThemeBreakpoints.md}) {
    font-size: ${({ theme }) => theme.text.displayLg.size};
  }
`;

/** The statement over the field, in white. */
export const AuthStatement = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.text.title.size};
  line-height: ${({ theme }) => theme.text.title.leading};
  letter-spacing: ${({ theme }) => theme.tracking.title};
  text-wrap: balance;
  color: ${({ theme }) => theme.color.bodyTextOnDark};
`;

/** A card's heading, in the display face at the website's heading step. */
export const AuthCardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  font-size: ${({ theme }) => theme.text.displaySm.size};
  line-height: ${({ theme }) => theme.text.displaySm.leading};
  letter-spacing: ${({ theme }) => theme.tracking.text};
`;

/** A sentence under a title or statement; it takes the colour around it unless given one. */
export const AuthLede = styled.p<{ color?: ThemeColor }>`
  margin: 0;
  color: ${({ theme, color }) => (color ? theme.color[color] : 'inherit')};
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  max-width: 640px;
  font-size: ${({ theme }) => theme.text.lede.size};
  line-height: ${({ theme }) => theme.text.lede.leading};
  letter-spacing: ${({ theme }) => theme.tracking.text};
  /* Plain wrapping: each line fills before it breaks. Balancing left a gap at the end. */
`;

/** A white card over the field, as the website's testimonial card. */
export const AuthCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 32px;
  border-radius: ${({ theme }) => theme.radius.card};
  background: ${({ theme }) => theme.color.surfaceCanvas};
  color: ${({ theme }) => theme.color.bodyTextPrimary};
`;

interface AuthLayoutProps {
  children: ReactNode;
  /** Which side the white panel sits on. */
  side?: AuthSide;
  /** The field's palette. */
  palette?: ShaderPalette;
  /** Set over the field, beside the panel, in white type. */
  aside?: ReactNode;
  /**
   * Draw the plasma field (default). Off, the page shows the still wash and needs no WebGL, as
   * the crash page should. Only an `AuthLayout` outside a stage reads it; a stage owns its field.
   */
  shader?: boolean;
}

const StagedLayout = ({
  children,
  side = 'left',
  palette = 'dusk',
  aside,
}: Omit<AuthLayoutProps, 'shader'>) => {
  const show = useContext(StageContext);
  const phase = useContext(PhaseContext);
  const foregroundRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (phase !== 'leave') show?.({ side, palette });
  }, [show, phase, side, palette]);

  // The page being left takes no focus or clicks. React 18 has no `inert` prop, so set the attribute.
  useLayoutEffect(() => {
    foregroundRef.current?.toggleAttribute('inert', phase === 'leave');
  }, [phase]);

  return (
    <Foreground ref={foregroundRef} phase={phase} aria-hidden={phase === 'leave' || undefined}>
      <Panel phase={phase}>
        <Brand>
          <Logo />
        </Brand>
        <Content phase={phase}>{children}</Content>
      </Panel>
      {aside && (
        <Aside phase={phase} side={side}>
          {aside}
        </Aside>
      )}
    </Foreground>
  );
};

const AuthLayout = ({ shader, ...props }: AuthLayoutProps) =>
  useContext(StageContext) ? (
    <StagedLayout {...props} />
  ) : (
    <AuthStage shader={shader}>
      <StagedLayout {...props} />
    </AuthStage>
  );

export default AuthLayout;
